import i18n, { type i18n as I18nInstance, type Resource } from "i18next";
import HttpApi from "i18next-http-backend";
import { initReactI18next } from "react-i18next";
import { SUPPORTED_LOCALE_LANGUAGES } from "@/constants/localization";
import { SupportedLanguages } from "@/enums/internationalization";
import { applyLocaleResources } from "@/i18n/applyLocaleResources";
import { enResources } from "@/i18n/enResources";
import type { LocaleResources } from "@/i18n/namespaces";

/**
 * Shared init options for the default singleton (`config.js`) and per-request
 * instances. English is always bundled; other languages are passed in or loaded
 * via HttpApi on the client.
 */
export function getAppI18nInitOptions(language: string, localeResources?: LocaleResources | null) {
  // Clone so per-request instances never mutate the shared bundled module
  // (i18next's store writes `addResource` into the objects it was given).
  const resources: Resource = {
    en: Object.fromEntries(
      Object.entries(enResources).map(([ns, bundle]) => [ns, structuredClone(bundle)])
    ),
  };

  if (
    localeResources &&
    language &&
    language !== SupportedLanguages.English
  ) {
    resources[language] = localeResources;
  }

  return {
    ns: Object.keys(enResources),
    lng: language,
    fallbackLng: "en" as const,
    supportedLngs: [...SUPPORTED_LOCALE_LANGUAGES],
    resources,
    partialBundledLanguages: true,
    debug: false,
    interpolation: {
      escapeValue: false,
    },
  };
}

/**
 * Create an isolated i18n instance for one React tree (SSR request or client
 * mount). Do not use the module singleton for request language — concurrent SSR
 * would otherwise leak `changeLanguage` / resources across users.
 */
export function createAppI18nInstance(
  language: string,
  localeResources?: LocaleResources | null
): I18nInstance {
  const instance = i18n.createInstance();
  // Resources are provided inline so init is ready for sync SSR `t()` calls.
  void instance.use(HttpApi).use(initReactI18next).init(getAppI18nInitOptions(language, localeResources));
  // Defensive: if resources were passed separately from init shape, register them.
  applyLocaleResources(instance, language, localeResources);
  return instance;
}
